// Calls settings (/settings/calls): device-local call tools — currently the
// echo test (record a few seconds, hear it back — verifies mic + speaker
// without needing a peer). Works offline and pre-registration, like every
// device-local section (prime directive: never block on the server).

import type { ReactNode } from 'react';
import { ECHO_RECORD_MS } from '@merkury/brain/call/media/echoTest.js';
import { useT } from '../../i18n/index.js';
import { useScreens } from '../ScreensContext.js';
import { useStore } from '../ui/useStore.js';
import { RingtoneSection, type RingtoneBridge } from './RingtoneSettingsScreen.js';
import { WebRingtoneSection } from './WebRingtonePicker.js';
import { Section, SettingsSubScreen, buttonClass, quietButtonClass } from './chrome.js';

function EchoTestSection(): ReactNode {
  const t = useT();
  const { echoTest } = useScreens();
  const state = useStore(echoTest.state);
  const running = state.phase === 'recording' || state.phase === 'playing';

  return (
    <Section title={t('settings.echoTest')}>
      <p className="text-xs text-muted">
        {t('settings.echoHelp', { secs: Math.round(ECHO_RECORD_MS / 1000) })}
      </p>
      {running ? (
        <>
          <p role="status" className="text-sm text-ink">
            {state.phase === 'recording' ? t('settings.recording') : t('settings.playingBack')}
          </p>
          <div
            role="meter"
            aria-label={t('settings.audioLevel')}
            aria-valuenow={Math.round(state.level * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            className="h-2 w-full overflow-hidden rounded-full bg-raised"
          >
            <div className="h-full bg-ok" style={{ width: `${Math.round(state.level * 100)}%` }} />
          </div>
          <button type="button" onClick={() => echoTest.cancel()} className={quietButtonClass}>
            {t('common.cancel')}
          </button>
        </>
      ) : (
        <button type="button" onClick={() => void echoTest.run()} className={buttonClass}>
          {state.phase === 'done' ? t('settings.runAgain') : t('settings.startEcho')}
        </button>
      )}
      {state.phase === 'done' && (
        <p role="status" className="text-sm text-ok">
          {t('settings.echoDone')}
        </p>
      )}
      {state.phase === 'error' && (
        <p role="alert" className="text-sm text-danger">
          {t('settings.echoFailed', { error: state.error ?? '' })}
        </p>
      )}
    </Section>
  );
}

export function CallsSettingsScreen({
  ringtone,
}: {
  /** Test seam forwarded to the native ringtone control. */
  ringtone?: RingtoneBridge;
} = {}): ReactNode {
  const t = useT();
  return (
    <SettingsSubScreen section="calls" title={t('settings.calls')}>
      <EchoTestSection />
      {/* Native uses the system ringtone picker; web uses the synth catalog.
          Each renders on exactly one platform. */}
      <RingtoneSection {...(ringtone ? { ringtone } : {})} />
      <WebRingtoneSection />
    </SettingsSubScreen>
  );
}
